import axios, { type AxiosInstance } from 'axios'
import { env } from '@config/env'
import { setupInterceptors } from './interceptors'
import { setStorageItem, removeStorageItem, STORAGE_KEYS, getAuthTokenFromStorage } from '@/lib/utils/storage'

/**
 * Determine base URL based on environment
 * In development, use empty string to leverage Vite proxy (avoids CORS)
 * In production, use the actual API URL
 */
const getBaseURL = (): string => {
  // Check if running in development mode (Vite dev server)
  const isDevelopment = import.meta.env.DEV
  
  // In development, use relative URLs to go through Vite proxy
  // This avoids CORS issues when calling the API from localhost
  if (isDevelopment) {
    return '' // Empty string means relative URLs, which Vite will proxy
  }
  
  // In production, use the full API URL
  return env.apiBaseUrl
}

// Create axios instance with environment configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: getBaseURL(),
  timeout: env.apiTimeout,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json, text/plain, */*',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
  },
})

// Setup interceptors
setupInterceptors(apiClient)

function syncAxiosDefaultTokenHeaders(token: string) {
  apiClient.defaults.headers.common['token'] = token
  apiClient.defaults.headers.common['Token'] = token
}

function clearAxiosDefaultTokenHeaders() {
  delete apiClient.defaults.headers.common['token']
  delete apiClient.defaults.headers.common['Token']
}

// Export typed API response
export interface ApiResponse<T = unknown> {
  data: T
  message?: string
  success: boolean
}

// Export typed API error
export interface ApiError {
  message: string
  status?: number
  errors?: Record<string, string[]>
}

/**
 * Set auth token for API requests
 * Note: The token is set in the interceptor as 'token' header, not 'Authorization'
 * @param token - JWT token
 */
export function setAuthToken(token: string | null): void {
  if (token) {
    setStorageItem(STORAGE_KEYS.authToken, token)
    try {
      localStorage.removeItem('authToken')
    } catch {
      /* ignore */
    }
    syncAxiosDefaultTokenHeaders(token)
  } else {
    delete apiClient.defaults.headers.common['Authorization']
    clearAxiosDefaultTokenHeaders()
    removeStorageItem(STORAGE_KEYS.authToken)
    try {
      localStorage.removeItem('authToken')
    } catch {
      /* ignore */
    }
  }
}

/**
 * Get current auth token (canonical `token` key + legacy `authToken` migration).
 */
export function getAuthToken(): string | null {
  return getAuthTokenFromStorage()
}

/**
 * Restore default axios `token` / `Token` headers from storage on load (interceptor still sets per request).
 */
export function initializeAuthToken(): void {
  const t = getAuthTokenFromStorage()
  if (t) syncAxiosDefaultTokenHeaders(t)
  else clearAxiosDefaultTokenHeaders()
}

// Initialize token on module load
initializeAuthToken()

export default apiClient

